/**
 * scanner.de — the German (Sie register) translation of the door check-in
 * scanner catalog. `scanner.en.ts` is the content source of truth here; this
 * file is its Sie translation, typed against `scanner.en.ts`'s shape via
 * `CatalogShape` so a missing or extra key here is a compile error, composed
 * into the shared `de` object by `messages.de.ts`.
 *
 * German is the operator's language at the venue this catalog serves, so
 * every line here is authored as native operator copy, not a gloss of the
 * English column — in particular the four verdict headlines
 * (`verdict.headline.*`) are chosen so none shares a word with another in
 * German either: Einlass / Bereits drin / Stopp / Warten.
 */
import type { CatalogShape } from "./catalog-shape.ts";
import type { scanner as scannerEn } from "./scanner.en.ts";

export const scanner: CatalogShape<typeof scannerEn> = {
	session: {
		title: "Einlass",
		subtitle: "Anmelden, um Tickets am Einlass zu scannen",
		notProvisioned:
			"Dieses Konto ist nicht für den Einlass eingerichtet. Wenden Sie sich an Ihre Veranstaltungsleitung.",
		noPermission:
			"Dieses Konto kann keine Tickets scannen. Wenden Sie sich an Ihre Veranstaltungsleitung.",
		signOut: "Abmelden",
		retrySignOut: "Erneut abmelden",
		pickEvent: "Veranstaltung wählen",
		noUpcomingEvents: "Keine anstehenden Veranstaltungen",
		noUpcomingEventsBody:
			"Bitten Sie eine Veranstaltungsleitung, eine anzulegen, bevor Sie jemanden einlassen können.",
		scanEvent: "Tickets scannen für {{name}}, {{date}}",
		scanEventWithStatus: "Tickets scannen für {{name}}, {{date}}, {{status}}",
		changeEvent: "Veranstaltung wechseln",
		noOrganization:
			"Öffnen Sie den Scanner über den Link, den Ihr Veranstalter Ihnen geschickt hat.",
		connecting: "Verbindung wird hergestellt…",
		offline: {
			title: "Keine Verbindung",
			body: "WLAN-Verbindung prüfen — wir versuchen es automatisch weiter.",
		},
	},
	scan: {
		cameraDenied:
			"Zum Scannen brauchen wir die Kamera. Bitte erlauben Sie den Zugriff und versuchen Sie es noch einmal.",
		retry: "Erneut versuchen",
		torchOn: "Taschenlampe einschalten",
		torchOff: "Taschenlampe ausschalten",
		torchFailed: "Blitzlicht nicht verfügbar. Erneut versuchen.",
		idle: "Kamera auf ein Ticket richten",
		checking: "Wird geprüft…",
		cameraError: {
			acquiring: "Kamera wird gestartet…",
			notAllowed: {
				title: "Die Kamera ist blockiert",
				body: "Erlauben Sie die Kamera in den Einstellungen Ihres Telefons und versuchen Sie es noch einmal.",
			},
			notFound: {
				title: "Keine Kamera gefunden",
				body: "Dieses Telefon hat keine Kamera, die wir nutzen können.",
			},
			notReadable: {
				title: "Eine andere App nutzt die Kamera",
				body: "Schließen Sie die App, die gerade die Kamera nutzt, und versuchen Sie es noch einmal.",
			},
			overconstrained: {
				title: "Diese Kamera kann das nicht",
				body: "Versuchen Sie es noch einmal — wir stellen die Kamera dann einfacher ein.",
			},
			decoderUnavailable: {
				title: "Der Scanner konnte nicht starten",
				body: "Laden Sie die Seite neu, um weiter zu scannen.",
			},
			tryAgain: "Erneut versuchen",
			reload: "Neu laden",
		},
		status: {
			modeAuto: "Auto-Einlass",
			modeManual: "Manueller Einlass",
			networkSlow: "Langsame Verbindung",
			networkOffline: "Offline",
			externalScanner: "Handscanner",
		},
		door: {
			noEvent: "Keine Veranstaltung gewählt",
			allTypes: "Alle Ticketarten",
			types: "{{count}} Ticketarten",
			now: "Jetzt",
			lane: "{{when}}: {{types}}",
		},
		findGuest: "Gast suchen",
		settings: "Einstellungen",
	},
	verdict: {
		pendingAdmit: {
			hold: "Zum Einlassen halten",
			holdAriaLabel: "Zum Einlassen halten — für diesen Gast gedrückt halten",
			verify: {
				hold: "Halten — ich habe geprüft",
				holdAriaLabel: "Zum Einlassen halten — Sie haben diesen Gast geprüft",
				checkFallback:
					"Prüfen Sie, ob dieser Gast für {{typeName}} in Frage kommt, bevor Sie ihn einlassen",
			},
		},
		valid: "Gültig — Einlass",
		alreadyRedeemed: "Bereits verwendet",
		refunded: "Erstattet — kein Einlass",
		invalid: "Ungültiges Ticket",
		offline: "Offline — Prüfung nicht möglich",
		headline: {
			admit: "Einlass",
			alreadyIn: "Bereits drin",
			stop: "Stopp",
			held: "Warten",
		},
		confirming: {
			default: "Wird bestätigt…",
			slow: "Prüfung läuft noch…",
		},
		admit: {
			partyRemaining: "{{count}} weitere aus dieser Bestellung",
			partyAlreadyInSuffix: " · {{count}} bereits drin",
		},
		holder: "Weitergegeben an {{name}}",
		alreadyIn: {
			self: "Ihre eigene Tür",
			stranger: "Bereits eingelassen",
			unknownDoor: "andere Tür",
			verify: "Prüfen",
		},
		stop: {
			wrongType: "{{typeName}} ist an diesem Eingang nicht gültig",
			wrongOrigin: "Dieses Ticket gehört an einen anderen Eingang",
			refunded: "Dieses Ticket wurde erstattet",
			tooLate: "Einlass geschlossen seit {{at}}",
			refused: "Einlass fehlgeschlagen — bitte erneut versuchen",
			unknown: "Unbekannter Code",
		},
		held: {
			offline: "Keine Verbindung — Prüfung nicht möglich",
			timeout: "Verbindung ist langsam — wir versuchen es weiter",
			body: "Bitten Sie die Person, kurz zur Seite zu treten — scannen Sie in der Zwischenzeit weiter.",
			retry: "Erneut versuchen",
			setAside: "Zur Seite bitten — weiter scannen",
		},
		verifyAtDoor: {
			holdToast:
				"{{typeName}} wird von einer Person bestätigt — bitte prüfen: {{note}}",
			noteFallback: "ob dieser Gast in Frage kommt, bevor Sie ihn einlassen",
		},
		undo: {
			action: "Einlass rückgängig machen",
			confirmTitle: "Diesen Einlass rückgängig machen?",
			confirmBody:
				"Das Ticket wird wieder gültig. Scannen Sie es erneut, um diesen Gast einzulassen.",
			confirm: "Rückgängig machen",
			cancel: "Beibehalten",
			success: "Einlass rückgängig gemacht",
		},
	},
	settings: {
		checkinMode: {
			label: "Einlassmodus",
			auto: "Auto",
			manual: "Manuell — jedes Ticket bestätigen",
		},
		acceptedTypes: "Was dieser Eingang einlässt",
		// Die Uhr statt des Einlassfensters: eine Uhr sieht man am Eingang
		// laufen, ein Fenster nicht. An einem mehrtägigen Lauf entscheidet auch
		// das Datum mit — das sagen die Tagesspalten darunter, und beides zu
		// nennen bricht auf einem schmalen Telefon um. "Automatisch" und
		// "Manuell" gehören dem Einlassmodus allein — zwei Achsen, ein Wort,
		// wäre auf derselben Zeile nicht auseinanderzuhalten.
		goesByTheTimeOfDay: "Richtet sich nach der Uhrzeit",
		goesByTheTimeOfDayAria: "Ticketarten richten sich nach der Uhrzeit",
		validNow: "Jetzt gültig",
		// "um", nie "seit": eine Uhrzeit ist keine Schwelle, die etwas
		// überschreitet, und "um 21:14" ist der Satz, mit dem eine Person einen
		// Zeitpunkt nennt.
		ownSelection: "Selbst ausgewählt",
		ownSelectionAt: "Selbst ausgewählt um {{time}}",
		typeOnDay: "{{name}} — {{day}}",
		allTypesOnDay: "Alle Ticketarten am {{day}}",
		allTypesWholeRun: "Alle Ticketarten für das ganze Event",
		// "Gasttickets" / "Shop-Tickets" stay this short because the options
		// render inside a chip row and in the one-line door readout.
		origin: {
			label: "Welche Tickets",
			all: "Alle",
			guests: "Gasttickets",
			sales: "Shop-Tickets",
		},
		admits: "Dieser Eingang lässt {{admitted}} von {{total}} Tickets ein",
		alwaysConfirmedSingular: "Bei {{names}} bestätigen Sie immer selbst",
		alwaysConfirmedTitle: "Diese bestätigen Sie immer selbst",
		sound: "Ton",
		haptics: "Vibration",
		back: "Zurück",
		close: "Schließen",
	},
	doorLog: {
		// "412 drin · 188 noch draußen" — beide Zahlen tragen ihr eigenes Wort,
		// damit die Zeile ohne Überschrift auskommt.
		in: "drin",
		stillOutside: "noch draußen",
		notConnected: "Keine Verbindung",
		stale: "Keine Verbindung — Zahlen können veraltet sein",
		nothingScanned: "Auf diesem Telefon wurde noch nichts gescannt",
		// Steht in einer einzeiligen Leiste, deshalb ein Wort statt
		// "Weitere Ticketarten".
		otherLane: "Andere",
		band: { justNow: "Gerade eben", thisHour: "Früher in dieser Stunde" },
		scope: {
			all: "Alles",
			refused: "Zu klären · {{count}}",
		},
		empty: {
			all: "Ihre Scans erscheinen hier, sobald Sie scannen",
			refused: "In dieser Schicht war bisher nichts zu klären",
		},
		outcome: {
			admitted: "Eingelassen",
			alreadyIn: "Bereits drin",
			notRecorded: "Nicht erfasst",
			refused: "Abgewiesen",
		},
		row: {
			ticket: "Ticket",
			noAnswer: "Keine Antwort",
			// Klein geschrieben, weil der Name davor weiterläuft. Der Name
			// selbst trägt kein eigenes Wort: eine Zeile mit einer Person zeigt
			// bereits, wessen Ticket es ist.
			boughtBy: "gekauft von: {{name}}",
		},
		detail: {
			partyRemaining: "{{count}} weitere kommen noch zu dieser Bestellung",
			admittedAtOtherDoor: "Eingelassen {{at}} · {{door}}",
			justScanned: "Sie haben das gerade eben gescannt",
			youAdmittedAt: "Sie haben das um {{at}} eingelassen",
			heldOffline: "Offline — dieser Scan wurde nie erfasst",
			heldTimeout: "Zeitüberschreitung — dieser Scan wurde nie erfasst",
		},
	},
	manualLookup: {
		searchPlaceholder: "Nach Name oder E-Mail suchen",
		escalateBody:
			"Suchen Sie eine Aufsichtsperson — auf deren Telefon lässt sich dieser Gast über den Namen finden. Dieses Telefon sieht {{count}} Tickets, aber keine Namen — das ist Absicht, damit Gästedaten nicht auf den Telefonen am Einlass liegen.",
		noMatch: "Kein Treffer.",
		noTypeMatch: "Kein Gast hält diese Ticketart.",
		clearFilterHint: "Filter aufheben, um alle zu sehen.",
		checkIn: "Einlassen",
		alreadyCheckedInBadge: "Bereits drin",
		checkedInToast: "Eingelassen — {{typeName}}",
		checkInFailed: "Einlass fehlgeschlagen — bitte erneut versuchen",
		backToScanning: "Zurück zum Scannen",
		jumpToLetter: "Zu {{letter}} springen",
		ticketCountSingular: "Ticket",
		ticketCountPlural: "Tickets",
		guestSheetAriaLabel: "Tickets von {{name}}",
	},
	ticketSheet: {
		pendingBadge: "Würde einlassen — unten bestätigen",
		alreadyInSelfBadge: "Sie haben dieses Ticket bereits eingelassen",
		alreadyInOtherBadge: "Bereits eingelassen",
		admittedBadge: "Eingelassen",
		party:
			"Gruppe von {{total}} — {{alreadyIn}} bereits drin, {{remaining}} kommen noch",
		admit: "Einlassen",
		reject: "Diesmal nicht",
		close: "Schließen",
	},
};
